"use client"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChevronDown, Plus, Settings, FolderPlus, Tags } from "@/lib/icons"
import { cn } from "@/lib/utils"
import type { SetGroup, TagSet } from "@/lib/tag-set-store"
import { useTranslations } from "next-intl"

interface SetGroupSelectorProps {
  groups: SetGroup[]
  activeGroupId: string | null
  sets: TagSet[]
  onGroupChange: (groupId: string | null) => void
  onCreateGroup: () => void
  onManageGroups: () => void
  onSetCreate: (tags: string[]) => void
  currentTags: string[]
  className?: string
  /** True only when the parent row measured itself as actually tight (see
   * tag-set-manager.tsx) — shortens "All Sets" to "All" for the default
   * (no group selected) label. Named groups are left alone; their names are
   * user-authored and out of scope here. */
  condensed?: boolean
}

export function SetGroupSelector({
  groups,
  activeGroupId,
  sets,
  onGroupChange,
  onCreateGroup,
  onManageGroups,
  onSetCreate,
  currentTags,
  className,
  condensed = false,
}: SetGroupSelectorProps) {
  const t = useTranslations("setGroupSelector")
  const activeGroup = groups.find((g) => g.id === activeGroupId)
  const activeGroupName = activeGroup ? activeGroup.name : condensed ? t("all") : t("allSets")

  const getSetCount = (groupId: string | null) => {
    if (groupId === null) return sets.length
    const group = groups.find((g) => g.id === groupId)
    return group ? group.setIds.length : 0
  }

  const groupedSetIds = new Set(groups.flatMap((g) => g.setIds))
  const ungroupedCount = sets.filter((s) => !groupedSetIds.has(s.id)).length

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {/* Group / view switcher */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 gap-1.5 bg-purple-500/15 border-purple-400/30 hover:bg-purple-500/30"
          >
            <span className="text-xs font-medium">{activeGroupName}</span>
            <span className="text-xs text-muted-foreground">({getSetCount(activeGroupId)})</span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem
            onClick={() => onGroupChange(null)}
            className={cn("cursor-pointer", activeGroupId === null && "bg-accent")}
          >
            <div className="flex items-center justify-between w-full">
              <span>{t("allSets")}</span>
              <span className="text-xs text-muted-foreground">({sets.length})</span>
            </div>
          </DropdownMenuItem>

          {ungroupedCount > 0 && (
            <DropdownMenuItem disabled className="text-muted-foreground">
              <div className="flex items-center justify-between w-full">
                <span className="text-xs">{t("ungrouped")}</span>
                <span className="text-xs">({ungroupedCount})</span>
              </div>
            </DropdownMenuItem>
          )}

          {groups.length > 0 && <DropdownMenuSeparator />}

          {groups.map((group) => (
            <DropdownMenuItem
              key={group.id}
              onClick={() => onGroupChange(group.id)}
              className={cn("cursor-pointer", activeGroupId === group.id && "bg-accent")}
            >
              <div className="flex items-center justify-between w-full">
                <span>{group.name}</span>
                <span className="text-xs text-muted-foreground">({group.setIds.length})</span>
              </div>
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={onManageGroups} className="cursor-pointer">
            <Settings className="h-3 w-3 mr-2" />
            <span>{t("manageGroups")}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Create dropdown — new set or new group */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 hover:bg-secondary/50 bg-secondary/40 border border-border/30"
            title={t("createNewSetOrGroup")}
          >
            <Plus className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuItem onClick={() => onSetCreate([])} className="cursor-pointer">
            <Tags className="h-3 w-3 mr-2" />
            {t("newSet")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onSetCreate(currentTags)}
            disabled={currentTags.length === 0}
            className="cursor-pointer"
          >
            <Tags className="h-3 w-3 mr-2" />
            {t("newSetWithCurrentTags")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onCreateGroup} className="cursor-pointer">
            <FolderPlus className="h-3 w-3 mr-2" />
            {t("newGroup")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
